<template>
  <div class="phone-tasks-list" data-testid="phone-tasks-list">
    <h1 class="phone-tasks-list__heading">Your Tasks</h1>

    <PhoneTaskState
      v-if="viewState === 'loading'"
      title="Loading tasks"
      data-testid="phone-tasks-loading"
    />
    <PhoneTaskState
      v-else-if="viewState === 'offline'"
      title="You're offline"
      text="Your tasks can't be shown without a connection. Reconnect to see them."
      action-label="Try again"
      data-testid="phone-tasks-offline"
      @action="load"
    />
    <PhoneTaskState
      v-else-if="viewState === 'error'"
      title="Couldn't load your tasks"
      text="Something went wrong while loading your tasks."
      action-label="Try again"
      data-testid="phone-tasks-error"
      @action="load"
    />
    <PhoneTaskState
      v-else-if="!tasks.length"
      title="No tasks assigned"
      data-testid="phone-tasks-empty"
    />
    <ul v-else class="phone-tasks-list__items" data-testid="phone-tasks-items">
      <li v-for="task in tasks" :key="task.key" class="phone-tasks-list__item">
        <PhoneTaskRow :task="task" />
      </li>
    </ul>
  </div>
</template>

<script setup>
import { onMounted, ref, watch } from "vue";
import PhoneTaskRow from "../../components/PhoneTaskRow/PhoneTaskRow.vue";
import PhoneTaskState from "../../components/PhoneTaskState/PhoneTaskState.vue";
import { useOnlineStatus } from "@/composables/useOnlineStatus";
import {
  fetchAllMyTasks,
  getCurrentAccountId,
  mapPhoneTask,
  sortPhoneTasks,
} from "@/utils/phoneTasks";

const { isOnline } = useOnlineStatus();

const viewState = ref("loading");
const tasks = ref([]);

const load = async () => {
  if (!isOnline.value) {
    tasks.value = [];
    viewState.value = "offline";
    return;
  }
  viewState.value = "loading";
  try {
    const raw = await fetchAllMyTasks(getCurrentAccountId());
    tasks.value = sortPhoneTasks(raw.map(mapPhoneTask));
    viewState.value = "ready";
  } catch (error) {
    console.error("Failed to load phone tasks", error);
    tasks.value = [];
    viewState.value = "error";
  }
};

watch(isOnline, (online, wasOnline) => {
  if (online && !wasOnline) load();
  if (!online && viewState.value !== "ready") viewState.value = "offline";
});

onMounted(load);
</script>

<style scoped>
.phone-tasks-list {
  box-sizing: border-box;
  width: 100%;
  min-height: 100%;
  padding: 16px;
  background: #f9fafb;
  font-family: "Inter", sans-serif;
}

.phone-tasks-list__heading {
  position: absolute;
  width: 1px;
  height: 1px;
  margin: -1px;
  padding: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.phone-tasks-list__items {
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin: 0;
  padding: 0;
  list-style: none;
}

.phone-tasks-list__item {
  display: block;
  min-width: 0;
}
</style>
